export function parseHttpUrl(input: string): URL | null {
  try {
    const url = new URL(input)
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      return null
    }
    return url
  } catch {
    return null
  }
}

export function transliterateFinnishDiacritics(input: string): string {
  const map: Record<string, string> = {
    ä: 'a',
    ö: 'o',
    å: 'a',
    Ä: 'A',
    Ö: 'O',
    Å: 'A',
  }

  return input.replace(/[äöåÄÖÅ]/g, (char) => map[char] || char)
}

function transliterateUrlPath(url: URL): URL {
  const pathParts = url.pathname.split('/')
  const transliteratedParts = pathParts.map((part) => transliterateFinnishDiacritics(part))
  const newPathname = transliteratedParts.join('/')

  const newUrl = new URL(url.toString())
  newUrl.pathname = newPathname
  return newUrl
}

async function testUrl(url: string): Promise<boolean> {
  try {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 5000)

    const response = await fetch(url, {
      method: 'HEAD',
      signal: controller.signal,
      headers: {
        'User-Agent':
          'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/138.0.0.0 Safari/537.36',
      },
    })

    clearTimeout(timeoutId)
    return response.ok || response.status === 405
  } catch {
    return false
  }
}

export async function resolveWorkingUrl(
  input: string,
  options?: {
    transliterate?: boolean
  }
): Promise<string | null> {
  const parsed = parseHttpUrl(input)

  if (!parsed) {
    return null
  }

  const transliterate = options?.transliterate ?? true
  const variants: string[] = []

  variants.push(input)

  try {
    const encodedUrl = parsed.toString()
    if (encodedUrl !== input) {
      variants.push(encodedUrl)
    }
  } catch (error) {
    console.error('Error encoding URL', error)
  }

  try {
    const decodedPath = decodeURIComponent(parsed.pathname)

    if (decodedPath !== parsed.pathname) {
      const decodedUrl = new URL(parsed.toString())
      decodedUrl.pathname = decodedPath
      variants.push(decodedUrl.toString())
    }
  } catch (error) {
    console.error('Error decoding URL', error)
  }

  if (transliterate) {
    try {
      const transliterated = transliterateUrlPath(parsed)
      const transliteratedStr = transliterated.toString()
      if (transliteratedStr !== input) {
        variants.push(transliteratedStr)
      }
    } catch (error) {
      console.error('Error transliterating URL', error)
    }
  }

  for (const variant of variants) {
    const works = await testUrl(variant)
    if (works) {
      return variant
    }
  }

  return variants[0] || null
}
